@if (!showResults) {
	<form [formGroup]="form" (ngSubmit)="onSave()">
		<h1 mat-dialog-title class="notification-title">
			{{ 'mes.administration.shift-approval.create-shift-note.dialog-header' | translate }}
		</h1>
		<mat-dialog-content class="dialog-content">
			<div class="dialog-spacer"></div>
			<!-- Date Range -->
			<mat-form-field appearance="outline" class="dialog">
				<mat-label>
					{{ 'mes.administration.shift-approval.create-shift-note.date-range.label' | translate }}
				</mat-label>
				<mat-date-range-input [rangePicker]="dateRangePicker" [formGroup]="form">
					<input matStartDate formControlName="startDate" readonly placeholder="{{ 'mes.administration.shift-approval.create-shift-note.date-range.start-placeholder' | translate }}" />
					<input matEndDate formControlName="endDate" readonly placeholder="{{ 'mes.administration.shift-approval.create-shift-note.date-range.end-placeholder' | translate }}" />
				</mat-date-range-input>
				<mat-datepicker-toggle matIconSuffix [for]="dateRangePicker"></mat-datepicker-toggle>
				<mat-date-range-picker #dateRangePicker></mat-date-range-picker>
			</mat-form-field>
			<!-- Work Task Selector -->
			<mat-form-field appearance="outline" class="dialog">
				<mat-label>
					{{ 'mes.administration.shift-approval.create-shift-note.worktask.label' | translate }}
				</mat-label>
				<mat-select formControlName="workTask" (openedChange)="onWorkTaskSelectOpened($event)">
					<div class="worktask-filter-container">
						<input
							#workTaskFilterInput
							class="worktask-filter-input"
							[placeholder]="'mes.administration.shift-approval.create-shift-note.worktask.filter-placeholder' | translate"
							[(ngModel)]="workTaskFilter"
							[ngModelOptions]="{ standalone: true }"
							(ngModelChange)="filterWorkTasks()"
							(keydown)="$event.stopPropagation()" />
					</div>
					<mat-option [value]="null">-</mat-option>
					@for (workTask of filteredWorkTasks; track trackByWorkTaskId($index, workTask)) {
						<mat-option [value]="workTask.id">({{ workTask.alias }}) {{ workTask.name }}</mat-option>
					}
				</mat-select>
			</mat-form-field>
			<!-- Team Profile Checkbox + Time Fields -->
			<div class="time-fields-row">
				<mat-checkbox formControlName="useTeamProfile">
					{{ 'mes.administration.shift-approval.create-shift-note.use-team-profile' | translate }}
				</mat-checkbox>
				<mat-form-field appearance="outline" class="dialog time-field">
					<mat-label>
						{{ 'mes.administration.shift-approval.create-shift-note.start-time.label' | translate }}
					</mat-label>
					<input matInput type="time" formControlName="startTime" />
				</mat-form-field>
				<mat-form-field appearance="outline" class="dialog time-field">
					<mat-label>
						{{ 'mes.administration.shift-approval.create-shift-note.end-time.label' | translate }}
					</mat-label>
					<input matInput type="time" formControlName="endTime" />
				</mat-form-field>
			</div>
			<!-- Validation Errors (only show after interaction) -->
			<div class="validation-errors">
				@if (form.hasError('sameStartAndEndError') && !form.get('useTeamProfile').value && form.get('startTime').dirty) {
					<mat-error>
						{{ 'mes.administration.shift-approval.create-shift-note.validation.same-time' | translate }}
					</mat-error>
				}
				@if (form.hasError('atLeastOneRequired') && (form.get('workTask').dirty || form.get('note').dirty)) {
					<mat-error>
						{{ 'mes.administration.shift-approval.create-shift-note.validation.at-least-one' | translate }}
					</mat-error>
				}
			</div>
			<!-- Note -->
			<mat-form-field appearance="outline" class="dialog">
				<mat-label>
					{{ 'mes.administration.shift-approval.create-shift-note.note.label' | translate }}
				</mat-label>
				<textarea matInput formControlName="note" maxlength="512" rows="3"></textarea>
			</mat-form-field>
			<!-- Employee Selector -->
			<mat-label class="mat-body-1">
				{{ 'mes.administration.shift-approval.create-shift-note.employees.label' | translate }}
			</mat-label>
			@if (items) {
				@if (items) {
					<app-grouped-multiselect [(items)]="items"></app-grouped-multiselect>
				}
			} @else {
				<div class="userselect-filler"></div>
			}
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-flat-button color="primary" type="submit" [disabled]="loading || form.invalid || !isItemSelected()" [ngClass]="{ 'disabled-button': loading }">
				{{ 'mes.administration.shift-approval.create-shift-note.buttons.save' | translate }}
			</button>
			<button mat-stroked-button type="button" (click)="onCancel()" (window:keyup.escape)="onCancel()" class="button-border-color">
				{{ 'mes.administration.shift-approval.create-shift-note.buttons.cancel' | translate }}
			</button>
		</mat-dialog-actions>
	</form>
} @else {
	<h1 mat-dialog-title class="notification-title">
		{{ 'mes.administration.shift-approval.create-shift-note.results.title' | translate }}
	</h1>
	<mat-dialog-content>
		<!-- Progress Bar -->
		@if (loading) {
			<div class="progress-section">
				<p class="progress-text">
					{{ 'mes.administration.shift-approval.create-shift-note.results.progress' | translate: { completed: progress.completed, total: progress.total } }}
				</p>
				<mat-progress-bar mode="determinate" [value]="progressPercent"></mat-progress-bar>
				<button mat-stroked-button type="button" (click)="onCancelProcessing()" class="button-border-color">
					{{ 'mes.administration.shift-approval.create-shift-note.buttons.cancel' | translate }}
				</button>
			</div>
		}
		<!-- Results Tables -->
		@if (!loading) {
			<!-- Errors/Skipped Table -->
			@if (failedResults.length) {
				<h3 class="results-section-header results-section-errors">
					{{ 'mes.administration.shift-approval.create-shift-note.results.sections.errors' | translate }}
					({{ failedResults.length }})
				</h3>
				<table class="results-table">
					<thead>
						<tr>
							<th>
								{{ 'mes.administration.shift-approval.create-shift-note.results.columns.employee' | translate }}
							</th>
							<th>
								{{ 'mes.administration.shift-approval.create-shift-note.results.columns.date' | translate }}
							</th>
							<th>
								{{ 'mes.administration.shift-approval.create-shift-note.results.columns.status' | translate }}
							</th>
							<th>
								{{ 'mes.administration.shift-approval.create-shift-note.results.columns.reason' | translate }}
							</th>
						</tr>
					</thead>
					<tbody>
						@for (result of failedResults; track result) {
							<tr [ngClass]="'status-' + result.status">
								<td>{{ result.employeeName }}</td>
								<td>{{ result.date | date: 'dd-MM-yyyy' }}</td>
								<td>
									{{ 'mes.administration.shift-approval.create-shift-note.results.status.' + result.status | translate }}
								</td>
								<td>
									@if (result.reason) {
										{{ 'mes.administration.shift-approval.create-shift-note.results.reasons.' + result.reason | translate }}
									}
									@if (result.reasonDetail) {
										({{ result.reasonDetail }})
									}
								</td>
							</tr>
						}
					</tbody>
				</table>
			}
			<!-- Success Table -->
			@if (successResults.length) {
				<h3 class="results-section-header results-section-success">
					{{ 'mes.administration.shift-approval.create-shift-note.results.sections.success' | translate }}
					({{ successResults.length }})
				</h3>
				<table class="results-table">
					<thead>
						<tr>
							<th>
								{{ 'mes.administration.shift-approval.create-shift-note.results.columns.employee' | translate }}
							</th>
							<th>
								{{ 'mes.administration.shift-approval.create-shift-note.results.columns.date' | translate }}
							</th>
							<th>
								{{ 'mes.administration.shift-approval.create-shift-note.results.columns.status' | translate }}
							</th>
						</tr>
					</thead>
					<tbody>
						@for (result of successResults; track result) {
							<tr class="status-success">
								<td>{{ result.employeeName }}</td>
								<td>{{ result.date | date: 'dd-MM-yyyy' }}</td>
								<td>
									{{ 'mes.administration.shift-approval.create-shift-note.results.status.success' | translate }}
								</td>
							</tr>
						}
					</tbody>
				</table>
			}
		}
	</mat-dialog-content>
	@if (!loading) {
		<mat-dialog-actions align="end">
			<button mat-stroked-button type="button" (click)="onCopyToClipboard()" class="button-border-color">
				{{ 'mes.administration.shift-approval.create-shift-note.buttons.copy-clipboard' | translate }}
			</button>
			<button mat-flat-button color="primary" type="button" (click)="onClose()">
				{{ 'mes.administration.shift-approval.create-shift-note.buttons.close' | translate }}
			</button>
		</mat-dialog-actions>
	}
}
